import { PF } from './tagFont';
import jsPDF from 'jspdf';
import { addSummaryPdf } from '@/lib/tagSummary';
import { imagePng, stampEstimatePdf } from '@/lib/tagExcel';
import { priceTag, pricingOn, getPricing, inr } from '@/lib/tagPricing';
import { rateCardById } from '@/lib/rateCards';

const M = { side: 10, top: 15, bottom: 9 };
const GAP = 5;
const PAD = 4;
const LINE = 4.8;
const MAX_CARD = 55;
const MIN_CARD = 38;
const PAGE = { portrait: [210, 297], landscape: [297, 210] };
const g3 = (n) => Number(n || 0).toFixed(3);

// Pick orientation + columns giving the fewest pages, then the biggest photos.
function plan(n, lines) {
  let best = null;
  Object.entries(PAGE).forEach(([orientation, [W, H]]) => {
    const uw = W - 2 * M.side; const uh = H - M.top - M.bottom;
    for (let cols = 1; cols <= 7; cols += 1) {
      const cw = Math.min(MAX_CARD, (uw - GAP * (cols - 1)) / cols);
      if (cw < MIN_CARD) break;
      const img = cw - 2 * PAD;
      const ch = PAD + img + 2 + lines * LINE + PAD;
      const rows = Math.floor((uh + GAP) / (ch + GAP));
      if (rows < 1) continue;
      const pages = Math.ceil(n / (cols * rows));
      const cand = { orientation, W, H, cols, rows, cw, ch, img, pages };
      if (!best || pages < best.pages || (pages === best.pages && (cw > best.cw + 0.5 || (Math.abs(cw - best.cw) <= 0.5 && cols > best.cols)))) best = cand;
    }
  });
  return best;
}

function fitText(pdf, text, maxW, size) {
  let s = size;
  pdf.setFontSize(s);
  while (pdf.getTextWidth(text) > maxW && s > 6) { s -= 0.5; pdf.setFontSize(s); }
  return text;
}

// A4 photo grid: each piece boxed with jewellery no., gross / net weight and (optionally) total value.
export async function buildImageListPdf(datas, rmMode, title, summary = false) {
  const priced = pricingOn();
  const card = priced ? rateCardById(getPricing().cardId) : null;
  const lines = priced ? 3 : 2;
  const p = plan(datas.length, lines);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: p.orientation, compress: true }); pdf.setFont(PF());
  const imgs = await Promise.all(datas.map((d) => imagePng(d.image)));
  const gridW = p.cols * p.cw + (p.cols - 1) * GAP;
  const x0 = (p.W - gridW) / 2;
  const per = p.cols * p.rows;
  const head = () => {
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(12); pdf.setTextColor(30, 42, 74);
    pdf.text(fitText(pdf, title || 'Image list', p.W - 2 * M.side - 40, 12), M.side, 10);
    pdf.setFont(PF(), 'normal'); pdf.setFontSize(8); pdf.setTextColor(100, 116, 139);
    pdf.text(`${datas.length} piece${datas.length === 1 ? '' : 's'} · ${new Date().toLocaleDateString('en-GB')}`, p.W - M.side, 10, { align: 'right' });
  };
  head();
  datas.forEach((d, i) => {
    if (i && i % per === 0) { pdf.addPage('a4', p.orientation); head(); }
    const k = i % per; const r = Math.floor(k / p.cols); const c = k % p.cols;
    const x = x0 + c * (p.cw + GAP); const y = M.top + r * (p.ch + GAP);
    pdf.setDrawColor(30, 42, 74); pdf.setLineWidth(0.35);
    pdf.roundedRect(x, y, p.cw, p.ch, 2, 2, 'S');
    const ix = x + PAD; const iy = y + PAD;
    pdf.setDrawColor(217, 222, 231); pdf.setLineWidth(0.2);
    pdf.rect(ix, iy, p.img, p.img, 'S');
    const im = imgs[i];
    if (im) {
      const s = Math.min(p.img / im.w, p.img / im.h);
      const w = im.w * s; const h = im.h * s;
      pdf.addImage(im.base64, 'PNG', ix + (p.img - w) / 2, iy + (p.img - h) / 2, w, h);
    } else {
      pdf.setFont(PF(), 'italic'); pdf.setFontSize(8); pdf.setTextColor(154, 163, 178);
      pdf.text('No photo', ix + p.img / 2, iy + p.img / 2, { align: 'center' });
    }
    const tw = p.cw - 2 * PAD;
    let ty = iy + p.img + 2 + 3.6;
    pdf.setFont(PF(), 'bold'); pdf.setTextColor(15, 107, 79);
    pdf.text(fitText(pdf, d.jewelleryNo || '', tw, 10), ix, ty);
    ty += LINE;
    pdf.setFont(PF(), 'normal'); pdf.setTextColor(30, 42, 74);
    pdf.text(fitText(pdf, `Gross ${g3(d.gross)} g   Net ${g3(d.net)} g`, tw, 8.5), ix, ty);
    if (priced) {
      ty += LINE;
      const t = priceTag(d, rmMode, card).total;
      pdf.setFont(PF(), 'bold');
      pdf.text(fitText(pdf, t == null ? 'Total value: rate missing' : `Total value: Rs. ${inr(t)}`, tw, 9), ix, ty);
    }
  });
  if (summary) addSummaryPdf(pdf, datas, rmMode, title);
  stampEstimatePdf(pdf, title);
  pdf.setProperties({ title: title || 'Image list' });
  return pdf;
}
